/**
 * Balatro 扑克系统配置
 * 牌型表 + 阈值 + Joker/Tarot/Planet 定义
 */

import type {
  PokerHandType,
  HandTypeLevel,
  Joker,
  PlanetCard,
  Suit,
  Rank,
  StakeholderId,
  CardPack,
  BlindType,
  BossEffect,
} from '../engine/types'

import { getSuitIconUrl, DOMAIN_COLORS } from './cardIcons'

// =============================================================================
// 花色映射
// =============================================================================

/** Stakeholder → Suit 技术映射 (pack 系统内部使用) */
export const STAKEHOLDER_TO_SUIT: Record<StakeholderId | 'synthesis', Suit> = {
  end_user: 'compute',
  pm: 'data',
  cto: 'infra',
  sales: 'network',
  synthesis: 'wild',
}

export const SUIT_DISPLAY: Record<Suit, { name: string; color: string; iconId: string; iconUrl: string }> = {
  compute:  { name: 'Compute',  color: DOMAIN_COLORS.compute,  iconId: 'lucide:cpu',      iconUrl: getSuitIconUrl('compute') },
  data:     { name: 'Data',     color: DOMAIN_COLORS.data,     iconId: 'lucide:database',  iconUrl: getSuitIconUrl('data') },
  network:  { name: 'Network',  color: DOMAIN_COLORS.network,  iconId: 'lucide:network',   iconUrl: getSuitIconUrl('network') },
  infra:    { name: 'Infra',    color: DOMAIN_COLORS.infra,    iconId: 'lucide:server',    iconUrl: getSuitIconUrl('infra') },
  wild:     { name: 'Wild',     color: DOMAIN_COLORS.wild,     iconId: 'lucide:sparkles',  iconUrl: getSuitIconUrl('wild') },
}

// =============================================================================
// 点数映射
// =============================================================================

/** depth → 对应的两个 rank 值 */
export const DEPTH_TO_RANKS: Record<number, Rank[]> = {
  1: [2, 3],
  2: [5, 7],
  3: [10, 13],  // 10, K
  0: [14],      // A (synthesis)
}

/** rank → chip 值 (Balatro standard) */
export const RANK_CHIP_VALUE: Record<Rank, number> = {
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
  7: 7,
  8: 8,
  9: 9,
  10: 10,
  11: 10,  // J
  12: 10,  // Q
  13: 10,  // K
  14: 11,  // A
}

/** rank 显示名 */
export const RANK_DISPLAY: Record<Rank, string> = {
  2: '2',
  3: '3',
  4: '4',
  5: '5',
  6: '6',
  7: '7',
  8: '8',
  9: '9',
  10: '10',
  11: 'J',
  12: 'Q',
  13: 'K',
  14: 'A',
}

/** 顺子排序序号 */
export const RANK_ORDER: Record<Rank, number> = {
  2: 0,
  3: 1,
  4: 2,
  5: 3,
  6: 4,
  7: 5,
  8: 6,
  9: 7,
  10: 8,
  11: 9,
  12: 10,
  13: 11,
  14: 12,
}

/** 所有 ranks (2-A) */
export const ALL_RANKS: Rank[] = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]

/** 4 花色 (不含 wild) */
export const ALL_SUITS: Suit[] = ['compute', 'data', 'network', 'infra']

/** 所有可能的顺子组合 (5 张连续) */
export const VALID_STRAIGHTS: Rank[][] = [
  [14, 2, 3, 4, 5],    // A-2-3-4-5 (ace low)
  [2, 3, 4, 5, 6],
  [3, 4, 5, 6, 7],
  [4, 5, 6, 7, 8],
  [5, 6, 7, 8, 9],
  [6, 7, 8, 9, 10],
  [7, 8, 9, 10, 11],
  [8, 9, 10, 11, 12],
  [9, 10, 11, 12, 13],
  [10, 11, 12, 13, 14], // 10-J-Q-K-A
]

// =============================================================================
// 牌型配置表 (标准 Balatro)
// =============================================================================

export type HandTypeConfig = {
  name: string
  nameCN: string
  baseChips: number
  baseMult: number
  upgradeChips: number
  upgradeMult: number
  /** 排序优先级 (越高越好) */
  priority: number
}

export const HAND_TYPE_CONFIG: Record<PokerHandType, HandTypeConfig> = {
  HIGH_CARD:       { name: 'High Card',       nameCN: '高牌',   baseChips: 5,   baseMult: 1,  upgradeChips: 10, upgradeMult: 1, priority: 0 },
  PAIR:            { name: 'Pair',             nameCN: '对子',   baseChips: 10,  baseMult: 2,  upgradeChips: 15, upgradeMult: 1, priority: 1 },
  TWO_PAIR:        { name: 'Two Pair',         nameCN: '两对',   baseChips: 20,  baseMult: 2,  upgradeChips: 20, upgradeMult: 1, priority: 2 },
  THREE_OF_A_KIND: { name: 'Three of a Kind',  nameCN: '三条',   baseChips: 30,  baseMult: 3,  upgradeChips: 20, upgradeMult: 2, priority: 3 },
  STRAIGHT:        { name: 'Straight',         nameCN: '顺子',   baseChips: 30,  baseMult: 4,  upgradeChips: 30, upgradeMult: 3, priority: 4 },
  FLUSH:           { name: 'Flush',            nameCN: '同花',   baseChips: 35,  baseMult: 4,  upgradeChips: 15, upgradeMult: 2, priority: 5 },
  FULL_HOUSE:      { name: 'Full House',       nameCN: '葫芦',   baseChips: 40,  baseMult: 4,  upgradeChips: 25, upgradeMult: 2, priority: 6 },
  FOUR_OF_A_KIND:  { name: 'Four of a Kind',   nameCN: '四条',   baseChips: 60,  baseMult: 7,  upgradeChips: 30, upgradeMult: 3, priority: 7 },
  STRAIGHT_FLUSH:  { name: 'Straight Flush',   nameCN: '同花顺', baseChips: 100, baseMult: 8,  upgradeChips: 40, upgradeMult: 4, priority: 8 },
  FIVE_OF_A_KIND:  { name: 'Five of a Kind',   nameCN: '五条',   baseChips: 120, baseMult: 12, upgradeChips: 35, upgradeMult: 3, priority: 9 },
}

/** 牌型初始等级 */
export function getInitialHandTypeLevels(): Record<PokerHandType, HandTypeLevel> {
  const levels = {} as Record<PokerHandType, HandTypeLevel>
  for (const [key, config] of Object.entries(HAND_TYPE_CONFIG)) {
    levels[key as PokerHandType] = {
      level: 1,
      baseChips: config.baseChips,
      baseMult: config.baseMult,
    }
  }
  return levels
}

// =============================================================================
// 游戏资源参数
// =============================================================================

export const POKER_CONFIG = {
  HAND_SIZE: 8,
  MAX_PLAY_CARDS: 5,
  INITIAL_HANDS: 5,
  INITIAL_DISCARDS: 3,
  MAX_JOKERS: 5,
  MAX_CONSUMABLES: 4,     // 消耗品上限
  MAX_PACKS: 2,           // 调研包上限
  SHOP_MONEY: 10,         // 初始金额
  PACK_COST: 3,           // 调研包价格
  REROLL_COST: 2,         // Joker 重掷价格
  JOKER_SHOP_SLOTS: 3,   // 上层展示 Joker 数量
} as const

/** Joker 价格映射 (按 category) */
export const JOKER_PRICE: Record<string, number> = {
  // chips 类 $2
  J01: 2, J02: 2, J03: 2, J04: 2,
  // mult 类 $3
  J05: 3, J06: 3, J07: 3,
  // x_mult 类 $4
  J08: 4, J09: 4, J10: 4,
  // conditional 类 $3
  J11: 3, J12: 3,
  // utility 类 $3
  J13: 3, J14: 3, J15: 3,
}

// =============================================================================
// 条款阈值 (分数里程碑)
// =============================================================================

export const CLAUSE_THRESHOLDS = [
  { clauseId: 'CL01', threshold: 150 },
  { clauseId: 'CL15', threshold: 300 },
  { clauseId: 'CL03', threshold: 225 },
  { clauseId: 'CL06', threshold: 450 },
  { clauseId: 'CL19', threshold: 200 },
  { clauseId: 'CL04', threshold: 400 },
  { clauseId: 'CL20', threshold: 600 },
] as const

// =============================================================================
// Blind 三局制配置
// =============================================================================

export const BLIND_CLAUSE_CONFIG: { type: BlindType; clauseIds: string[] }[] = [
  { type: 'small_blind', clauseIds: ['CL01', 'CL15'] },
  { type: 'big_blind',   clauseIds: ['CL03', 'CL06'] },
  { type: 'boss',        clauseIds: ['CL19', 'CL04', 'CL20'] },
]

export const BLIND_LABELS: Record<BlindType, { en: string; cn: string }> = {
  small_blind: { en: 'Small Blind', cn: '小盲' },
  big_blind:   { en: 'Big Blind',   cn: '大盲' },
  boss:        { en: 'Boss',        cn: 'Boss' },
}

export const INTER_SHOP_MONEY = 5

export const BLIND_REWARDS: Record<BlindType, number> = {
  small_blind: 3,
  big_blind: 4,
  boss: 5,
}

export const MAX_INTEREST = 5

// =============================================================================
// Boss 效果池 (8 个 — 随机选 1)
// =============================================================================

export const BOSS_EFFECTS: Omit<BossEffect, 'debuffedSuit'>[] = [
  {
    id: 'BE01', name: 'Firewall',       nameCN: '防火墙',
    description: 'Cards of one random suit do not score',
    descriptionCN: '随机一种花色的牌不计分',
    type: 'debuff_suit',
  },
  {
    id: 'BE02', name: 'Access Control', nameCN: '权限管控',
    description: 'Face cards (K) do not score',
    descriptionCN: '面牌 (K) 不计分',
    type: 'debuff_face',
  },
  {
    id: 'BE03', name: 'Protocol Clash', nameCN: '协议冲突',
    description: 'Cannot repeat the same hand type',
    descriptionCN: '不能重复打同一牌型',
    type: 'no_repeat_hand',
  },
  {
    id: 'BE04', name: 'Single Entry',   nameCN: '单一入口',
    description: 'Can only play one hand type (decided by first hand)',
    descriptionCN: '整局只能打一种牌型（第一手决定）',
    type: 'one_hand_type',
  },
  {
    id: 'BE05', name: 'Full Audit',     nameCN: '全量审计',
    description: 'Must play exactly 5 cards each hand',
    descriptionCN: '每手必须出满5张牌',
    type: 'must_play_5',
  },
  {
    id: 'BE06', name: 'Emergency Freeze', nameCN: '紧急冻结',
    description: 'No discards this round',
    descriptionCN: '本局没有弃牌次数',
    type: 'no_discards',
  },
  {
    id: 'BE07', name: 'Resource Cut',   nameCN: '资源缩减',
    description: 'Hand size reduced by 1',
    descriptionCN: '手牌上限 -1',
    type: 'minus_hand_size',
  },
  {
    id: 'BE08', name: 'Degradation',    nameCN: '性能衰减',
    description: 'Base chips and mult are halved',
    descriptionCN: '牌型基础 chips 和 mult 减半',
    type: 'halve_base',
  },
]

/** 条款计分 (用于最终 0-100 分) */
export const CLAUSE_POINTS: Record<string, number> = {
  CL01: 8,
  CL15: 8,
  CL03: 10,
  CL06: 10,
  CL19: 12,
  CL04: 14,
  CL20: 20,
}

// =============================================================================
// Joker 定义 (15 张)
// =============================================================================

export const ALL_JOKERS: Joker[] = [
  // Chips 类
  { id: 'J01', name: '关键证人',   description: '+15 Chips',                              category: 'chips',       unlockCondition: '访谈任意 2 个 stakeholder depth 1',  unlocked: false },
  { id: 'J02', name: '深度调查',   description: 'depth 3 卡(K) 计分时 +15 Chips',          category: 'chips',       unlockCondition: '任意 stakeholder 到 depth 3',        unlocked: false },
  { id: 'J03', name: '审计线索',   description: 'Ace 计分时 +30 Chips',                    category: 'chips',       unlockCondition: '解锁 3+ 推理题',                     unlocked: false },
  { id: 'J04', name: '内部消息',   description: '牌组剩余卡数 × +1 Chips',                  category: 'chips',       unlockCondition: 'Sales depth 3',                      unlocked: false },
  // Mult 类
  { id: 'J05', name: '法律顾问',   description: '+4 Mult',                                 category: 'mult',        unlockCondition: '访谈 PM depth 2+',                   unlocked: false },
  { id: 'J06', name: '判例援引',   description: '含 Pair 时 +3 Mult',                      category: 'mult',        unlockCondition: '访谈 PM depth 1',                    unlocked: false },
  { id: 'J07', name: '谈判专家',   description: '每打出一手牌 +2 Mult（递增）',               category: 'mult',        unlockCondition: '发现全部 7 条款 0 误点',              unlocked: false },
  // ×Mult 类
  { id: 'J08', name: '证据链',     description: '3+ 同花色计分时 ×1.5 Mult',                category: 'x_mult',      unlockCondition: '同一 stakeholder 3+ 张卡',            unlocked: false },
  { id: 'J09', name: '合同漏洞',   description: 'Two Pair 或 Full House 时 ×1.5 Mult',     category: 'x_mult',      unlockCondition: '解锁 RQ04 或 RQ05',                  unlocked: false },
  { id: 'J10', name: '尽职调查',   description: '×1.5 Mult（已过 2+ 阈值时）',               category: 'x_mult',      unlockCondition: '访谈全部 4 stakeholder',              unlocked: false },
  // 条件类
  { id: 'J11', name: '交叉质询',   description: '出牌中每种不同花色 +3 Mult',                category: 'conditional', unlockCondition: '收集 3+ 不同来源卡',                  unlocked: false },
  { id: 'J12', name: '万能证人',   description: 'Wild 卡同时匹配任意点数',                   category: 'conditional', unlockCondition: '收集 4+ 合成卡',                      unlocked: false },
  // 实用类
  { id: 'J13', name: '不可抗力',   description: '+1 弃牌次数',                              category: 'utility',     unlockCondition: 'CTO depth 2+',                       unlocked: false },
  { id: 'J14', name: '缓兵之计',   description: '+1 出牌次数',                              category: 'utility',     unlockCondition: '进入质证时手牌 ≥ 8 张',               unlocked: false },
  { id: 'J15', name: '和解方案',   description: '分数达到阈值 85% 也算突破',                 category: 'utility',     unlockCondition: '收集 20+ 张卡',                       unlocked: false },
]

// =============================================================================
// 卡包商店定义 (4 种卡包)
// =============================================================================

export const ALL_PACKS: CardPack[] = [
  {
    id: 'user_interview',
    name: '用户访谈',
    icon: 'lucide:file-text',
    description: '来自终端用户的一手反馈',
    deckSource: 'end_user',
    cards: [
      { id: 'C01', name: '上传风暴记录', description: '销毁2张卡，+1出牌', effectType: 'destroy_for_hand', targetCount: 2, packId: 'user_interview' },
      { id: 'C02', name: '公开链接泄露', description: '从弃牌堆取回3张',   effectType: 'retrieve',         targetCount: 0, packId: 'user_interview' },
      { id: 'C03', name: '用量分级方案', description: '+1 弃牌次数',       effectType: 'plus_discard',     targetCount: 0, packId: 'user_interview' },
    ],
  },
  {
    id: 'expert_interview',
    name: '专家访谈',
    icon: 'lucide:monitor',
    description: '来自技术专家的深度分析',
    deckSource: 'cto',
    cards: [
      { id: 'C04', name: 'CDN架构方案',   description: '1张卡 Foil (+30 chips)', effectType: 'foil',    targetCount: 1, packId: 'expert_interview' },
      { id: 'C05', name: '峰值压测报告',   description: '1张卡 Holo (+4 mult)',   effectType: 'holo',    targetCount: 1, packId: 'expert_interview' },
      { id: 'C06', name: '自动化脚本',     description: '1张卡点数+1级',          effectType: 'rank_up', targetCount: 1, packId: 'expert_interview' },
    ],
  },
  {
    id: 'industry_report',
    name: '行业报告',
    icon: 'lucide:bar-chart-3',
    description: '权威行业数据与标准',
    deckSource: 'pm',
    cards: [
      { id: 'C07', name: 'SLA行业基准',   description: '+1 出牌次数',           effectType: 'plus_hand', targetCount: 0, packId: 'industry_report' },
      { id: 'C08', name: '云服务定价报告', description: '复制1张卡加入牌组',      effectType: 'copy_card', targetCount: 1, packId: 'industry_report' },
      { id: 'C09', name: '合规审计指南',   description: '1张卡变wild花色',       effectType: 'wild',      targetCount: 1, packId: 'industry_report' },
    ],
  },
  {
    id: 'competitor_analysis',
    name: '竞对分析',
    icon: 'lucide:clipboard-list',
    description: '竞争对手的产品与定价情报',
    deckSource: 'sales',
    cards: [
      { id: 'C10', name: '竞品功能对比',   description: '改变1张卡花色',          effectType: 'change_suit', targetCount: 1, packId: 'competitor_analysis' },
      { id: 'C11', name: '竞品定价策略',   description: '1张卡 Foil (+30 chips)', effectType: 'foil',        targetCount: 1, packId: 'competitor_analysis' },
      { id: 'C12', name: '客户流失报告',   description: '1张卡点数+1级',          effectType: 'rank_up',     targetCount: 1, packId: 'competitor_analysis' },
    ],
  },
]

// =============================================================================
// 星球牌定义 (6 张 — 推理题自动升级)
// =============================================================================

// =============================================================================
// 牌型排列顺序 (高 → 低)
// =============================================================================

export const HAND_TYPE_ORDER: PokerHandType[] = [
  'STRAIGHT_FLUSH', 'FIVE_OF_A_KIND', 'FOUR_OF_A_KIND', 'FULL_HOUSE',
  'FLUSH', 'STRAIGHT', 'THREE_OF_A_KIND', 'TWO_PAIR', 'PAIR', 'HIGH_CARD',
]

// =============================================================================
// 星球牌定义 (6 张 — 推理题自动升级)
// =============================================================================

export const ALL_PLANETS: PlanetCard[] = [
  { id: 'P01', name: '水星 Mercury', upgradesHandType: 'PAIR',            chipsPerLevel: 15, multPerLevel: 1, reasoningQuestionId: 'RQ01' },
  { id: 'P02', name: '金星 Venus',   upgradesHandType: 'THREE_OF_A_KIND', chipsPerLevel: 20, multPerLevel: 2, reasoningQuestionId: 'RQ02' },
  { id: 'P03', name: '地球 Earth',   upgradesHandType: 'FLUSH',           chipsPerLevel: 15, multPerLevel: 2, reasoningQuestionId: 'RQ03' },
  { id: 'P04', name: '火星 Mars',    upgradesHandType: 'FULL_HOUSE',      chipsPerLevel: 25, multPerLevel: 2, reasoningQuestionId: 'RQ04' },
  { id: 'P05', name: '木星 Jupiter', upgradesHandType: 'STRAIGHT',        chipsPerLevel: 30, multPerLevel: 3, reasoningQuestionId: 'RQ05' },
  { id: 'P06', name: '土星 Saturn',  upgradesHandType: 'FOUR_OF_A_KIND',  chipsPerLevel: 30, multPerLevel: 3, reasoningQuestionId: 'RQ06' },
]
